import { CSRF_HEADER, type ProjectShareState } from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock, Users, UserX } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { api } from "../api.ts";
import { ok } from "../api-errors.ts";
import { navigate, projectHomePath } from "../router.tsx";
import { useSession } from "../session.tsx";
import { conversationKeys } from "./queries.ts";

/**
 * "Share with the project" (openspec change `project-conversation-share`): one row above the
 * named shares. The project share reaches every member who can read the project (readers and
 * above, now and later); each of them still has to pass every source of the conversation, so
 * the row says how many can't, as counts only: never which document or which person.
 */
export function ProjectShareSection(props: {
  conversationId: string;
  /** A frozen project shows the state but takes no change. */
  frozen: boolean;
}) {
  const { conversationId, frozen } = props;
  const { t } = useTranslation();
  const id = useId();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const key = conversationKeys.projectShare(conversationId);
  const param = { conversationId };
  const state = useQuery({
    queryKey: key,
    queryFn: async () =>
      ok(await api.api.conversations[":conversationId"]["project-share"].$get({ param })),
  });
  const change = useMutation({
    mutationFn: async (on: boolean) => {
      const route = api.api.conversations[":conversationId"]["project-share"];
      const options = { headers: { [CSRF_HEADER]: csrfToken } };
      return ok(await (on ? route.$put({ param }, options) : route.$delete({ param }, options)));
    },
    onSuccess: (next) => queryClient.setQueryData(key, next),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: conversationKeys.lists });
      return queryClient.invalidateQueries({
        queryKey: conversationKeys.detail(conversationId),
        exact: true,
      });
    },
  });
  const data = state.data;
  const shared = data?.sharedAt != null;

  return (
    <section aria-labelledby={`${id}-heading`} className="flex flex-col gap-2">
      <h3 id={`${id}-heading`} className="text-sm font-medium text-fg">
        {t("projectShare.heading")}
      </h3>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-line bg-surface-raised px-3 py-2.5">
        <span
          aria-hidden="true"
          className="flex size-8 shrink-0 items-center justify-center rounded-md bg-surface-sunken text-fg-secondary"
        >
          <Users className="size-4" strokeWidth={1.75} />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <p className="text-sm font-medium text-fg">
            {shared ? t("projectShare.shared") : t("projectShare.everyone")}
          </p>
          <p aria-live="polite" className="text-xs text-fg-muted">
            {data ? <Counts state={data} /> : state.isPending ? t("app.loading") : null}
          </p>
        </div>
        {frozen || !data ? null : (
          <Button
            type="button"
            variant={shared ? "ghost" : "outline"}
            size="sm"
            className="ml-auto"
            disabled={change.isPending}
            onClick={() => change.mutate(!shared)}
          >
            {change.isPending
              ? t("projectShare.working")
              : shared
                ? t("projectShare.stop")
                : t("projectShare.share")}
            {/* The dialog's other "Share" names people: this one says what it shares with. */}
            <span className="sr-only"> {t("projectShare.withProject")}</span>
          </Button>
        )}
      </div>
      <p className="text-xs text-fg-muted">
        {frozen ? t("projectShare.frozen") : t("projectShare.guests")}
      </p>
      {state.error || change.error ? (
        <Notice tone="danger">{(change.error ?? state.error)?.message}</Notice>
      ) : null}
    </section>
  );
}

/**
 * The audience and how many of it can't read the conversation: "3 of 12 members can't read some
 * of its sources", or "12 members will be able to read it" (before) / "can read it" (after).
 */
function Counts({ state }: { state: ProjectShareState }) {
  const { t } = useTranslation();
  const shared = state.sharedAt !== null;
  if (state.audience === 0) {
    return <>{t("projectShare.nobodyYet")}</>;
  }
  if (state.excluded === 0) {
    return (
      <span className="tabular-nums">
        {shared
          ? t("projectShare.allRead", { count: state.audience })
          : t("projectShare.allWillRead", { count: state.audience })}
      </span>
    );
  }
  return (
    // The lock stays beside the first line when the sentence wraps.
    <span className="flex items-start gap-1 tabular-nums">
      <Lock aria-hidden="true" className="mt-px size-3.5 shrink-0" strokeWidth={1.75} />
      {/* Plural agrees with the excluded count, the subject of the sentence. */}
      <span>
        {t("projectShare.someExcluded", { count: state.excluded, total: state.audience })}
      </span>
    </span>
  );
}

/**
 * A project owner or manager reading a conversation someone else shared with the project stops
 * that share (#170: its creator may have left). It only narrows: named shares stay, and only
 * the creator can share it with the project again. They may lose the conversation with it, so
 * the project opens afterwards.
 */
export function StopProjectShareAction(props: { conversationId: string; projectId: string }) {
  const { conversationId, projectId } = props;
  const { t } = useTranslation();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const stop = useMutation({
    mutationFn: async () =>
      ok(
        await api.api.conversations[":conversationId"]["project-share"].$delete(
          { param: { conversationId } },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
    onSuccess: () => {
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: conversationKeys.lists });
      navigate(projectHomePath(projectId));
    },
  });
  const label = t("projectShare.adminStop");
  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={label}
            onClick={() => setOpen(true)}
            className="text-fg-muted hover:text-fg"
          >
            <UserX aria-hidden="true" strokeWidth={1.75} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
      <AppDialog
        open={open}
        onClose={() => setOpen(false)}
        title={t("projectShare.adminStopTitle")}
        description={t("projectShare.adminStopBody")}
      >
        {stop.error ? <Notice tone="danger">{stop.error.message}</Notice> : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setOpen(false)}>
            {t("settings.cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={stop.isPending}
            onClick={() => stop.mutate()}
          >
            {stop.isPending ? t("projectShare.working") : t("projectShare.adminStopConfirm")}
          </Button>
        </DialogFooter>
      </AppDialog>
    </>
  );
}
